import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  previewNotification,
  type NotificationPreview,
  type NotificationRecipient,
} from '@/lib/notificationSend.ts';
import type { NotificationKindChoice } from './useNotificationKind.ts';
import type { PeopleSelection } from './usePeopleSelection.ts';
import type { SendOptions } from './useSendOptions.ts';

export interface PreviewState {
  /** Whose preview is drawn; everybody gets the same message in their own language. */
  previewRecipient: NotificationRecipient | null;
  canPreview: boolean;
  preview: UseQueryResult<NotificationPreview>;
}

interface PreviewInput {
  kind: NotificationKindChoice;
  options: SendOptions;
  recipient: NotificationRecipient | null;
  people: NotificationRecipient[];
  selection: PeopleSelection;
  meetingId: string;
}

/* The person staff asked to see, or else the first one ticked who would get it. */
function listPreviewRecipient(
  people: NotificationRecipient[],
  selection: PeopleSelection,
): NotificationRecipient | null {
  return (
    people.find((person) => person.user_id === selection.previewForId) ??
    selection.selected.find((person) => person.skip_reason === null) ??
    selection.selected[0] ??
    null
  );
}

export function useNotificationPreview({
  kind,
  options,
  recipient,
  people,
  selection,
  meetingId,
}: PreviewInput): PreviewState {
  const previewRecipient = kind.isUserScope
    ? listPreviewRecipient(people, selection)
    : recipient;

  const canPreview =
    kind.kind !== '' &&
    previewRecipient !== null &&
    (kind.isUserScope || meetingId !== '') &&
    (!options.takesRoleplay || options.roleplayId !== undefined);

  const preview = useQuery({
    queryKey: [
      'notificationPreview',
      kind.kind,
      previewRecipient?.user_id,
      meetingId,
      options.neverName,
      options.roleplayId,
      options.channel,
      options.resend,
    ],
    queryFn: () =>
      previewNotification({
        kind: kind.kind,
        userId: previewRecipient!.user_id,
        meetingId: kind.isUserScope ? undefined : meetingId,
        neverName: options.neverName,
        roleplayId: options.roleplayId,
        channel: options.channel,
        resend: options.resend,
      }),
    enabled: canPreview,
  });

  return { previewRecipient, canPreview, preview };
}
